import React, { useEffect } from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TurnoverItem, turnoverSchema } from "./types/TurnoverRulesTypes";
import { BaseTable, Button, Card, HeaderMenu, Input, NumberInput, TableHeader } from "components";
import { useTurnoverRules } from "./hooks/useListTurnoverRules";
import toast from "react-hot-toast";
import { useApi } from "utils";


type FormValues = {
  items: TurnoverItem[];
};

const RULES = [
  { code: "01", rule: "DEAD STOCK" },
  { code: "02", rule: "SLOW" },
  { code: "03", rule: "NORMAL" },
  { code: "04", rule: "FAST MOVING" }
];

const InventoryTurnoverRules: React.FC = () => {

  const api = useApi();
  const { data: apiData, isLoading, refetch } = useTurnoverRules();

  const { control, handleSubmit, setValue, reset } = useForm<FormValues>({
    resolver: yupResolver(turnoverSchema),
    defaultValues: {
      items: mapApiToFormItems()
    }
  });

  useEffect(() => {
    if (isLoading) return;
  
    if (apiData.length === 0) return;
  
    reset({
      items: mapApiToFormItems(apiData)
    });
    // eslint-disable-next-line
  }, [apiData, isLoading, reset]);
  
  const { fields } = useFieldArray({
    control,
    name: "items"
  });

  const items = useWatch({ control, name: "items" });

  useEffect(() => {
    if (!items) return;
  
    items.forEach((item, index) => {
      let nextMin = 0;
  
      if (index > 0) {
        const prevMax = items[index - 1]?.max_value;
        if (typeof prevMax === "number") {
          nextMin = Number((prevMax + 0.01).toFixed(2));
        }
      }
  
      if (item.min_value !== nextMin) {
        setValue(`items.${index}.min_value`, nextMin, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, setValue]);

  const onSubmit = (data: FormValues) => {
    const payload = data.items.map((item) => ({
      mr_product_turnover_code: item.mr_product_turnover_code,
      min_value: item.min_value,
      max_value: item.max_value,
      description: item.description
    }));

    return new Promise<void>((resolve) => {
      toast.promise(
        api.post("/inventory_turnover_rule/inventory-turnover-rule/upsert", payload),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        }
      ).then(() => {
        refetch()
      }).catch(() => {}).finally(resolve)
    })
  };

  return (
    <section className="container pt-5  gap-5 flex flex-col">
      <HeaderMenu title="INVENTORY TURNOVER RULES"  className="sticky top-[-25px] z-[2]"/>

      <Card>
        <Card.Body> 
          <BaseTable>
            <TableHeader>
              <tr>
                <th>CODE</th>
                <th>RULE</th>
                <th>MIN</th>
                <th>MAX</th>
                <th>DESCRIPTION</th>
              </tr>
            </TableHeader>
              <tbody>
                {fields.map((field, index) => {
                  return (
                    <tr key={field.id}>
                      <td className="text-center">{field.mr_product_turnover_code}</td>
                      <td>{field.rule}</td>

                      {/* MIN */}
                      <td>
                        <Controller
                            control={control}
                            name={`items.${index}.min_value`}
                            render={({ field, fieldState }) => (
                                <NumberInput
                                    inputClass=""
                                    readOnly
                                    error={fieldState.error?.message}
                                    onValueChange={(value) => {
                                        field.onChange(value.floatValue ?? 0);
                                    }}
                                    value={field.value ?? 0}
                                    textRight={true}
                                />
                            )}
                        />
                      </td>

                      {/* MAX */}
                      <td>
                        {field.mr_product_turnover_code === "04" ? (
                          <div className="text-center font-medium text-gray-500">
                            <Input type="text" disabled inputClass="bg-gray text-right" value={"Unlimited"}/>
                          </div>
                        ) : (
                          <Controller
                            name={`items.${index}.max_value`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <NumberInput
                                inputClass=""
                                error={fieldState.error?.message}
                                onValueChange={(e) => {
                                  field.onChange(
                                    e.value === "" ? null : Number(e.value)
                                  );
                                }}
                                value={field.value ?? ""}
                                textRight
                              />
                            )}
                          />
                        )}
                      </td>

                      {/* DESCRIPTION */}
                      <td>
                        <Controller
                          name={`items.${index}.description`}
                          control={control}
                          render={({ field, fieldState }) => (
                              <Input
                                type="text"
                                value={field.value ?? ''}
                                onChange={(value) => {
                                    field.onChange(value)
                                }}
                                error={fieldState.error?.message}
                              />
                          )}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </BaseTable>
          </Card.Body>
      </Card>

      {/*  SUBMIT MANUAL */}
      <div className="mt-3 flex justify-end">
        <Button
            color="primary"
            onClick={handleSubmit(onSubmit)}
        >
            SAVE
        </Button>
      </div>
    </section>
  );
};

const mapApiToFormItems = (
  apiData: TurnoverItem[] = []
): TurnoverItem[] => {
  return RULES.map((r) => {
    const fromApi = apiData.find(
      (d) => d.mr_product_turnover_code === r.code
    );

    if (r.code === "04") {
      return {
        mr_product_turnover_code: r.code,
        rule: r.rule,
        min_value: fromApi?.min_value ?? 0,
        max_value: null, 
        description: fromApi?.description ?? ""
      };
    }

    return {
      mr_product_turnover_code: r.code,
      rule: r.rule,
      min_value: fromApi?.min_value ?? 0,
      max_value: fromApi?.max_value ?? null,
      description: fromApi?.description ?? ""
    };
  });
};

export default InventoryTurnoverRules;
